"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { AuthenticationService } from "@/services/AuthenticationService";
import { ErrorHandlerService } from "@/services/ErrorHandlerService";

interface ForgotPasswordFormProps {
  language: string;
  translations?: Record<string, string>;
  onSuccess?: () => void;
  onBack?: () => void;
}

export default function ForgotPasswordForm({
  language,
  translations = {},
  onSuccess,
  onBack,
}: ForgotPasswordFormProps) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const t = (key: string, defaultValue: string) => {
    return translations[key] || defaultValue;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await AuthenticationService.forgotPassword({
        email,
        language,
      });

      if (!response.success) {
        const errorMessage = ErrorHandlerService.extractErrorFromResponse(
          response,
          t("auth.forgot_password.error.message", "Failed to send password reset email")
        );
        throw new Error(errorMessage);
      }

      setSuccess(true);
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (err) {
      const errorMessage = ErrorHandlerService.cleanErrorMessage(
        err,
        t("auth.forgot_password.error.message", "Failed to send password reset email")
      );
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4"
      >
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          <h3 className="font-semibold mb-2">
            {t("auth.forgot_password.success.title", "Email Sent")}
          </h3>
          <p className="text-sm">
            {t(
              "auth.forgot_password.success.message",
              "We've sent a password reset link to your email address. Please check your inbox."
            )}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div>
        <label
          htmlFor="forgot-email"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          {t("auth.forgot_password.form.email", "Email Address")}
        </label>
        <input
          type="email"
          id="forgot-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors bg-gray-50"
          placeholder={t("auth.forgot_password.form.email_placeholder", "Enter your email")}
        />
      </div>

      <div className="flex gap-2">
        {onBack && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
          >
            {t("auth.forgot_password.form.back", "Back")}
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="flex-1 bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed uppercase font-semibold transition-colors"
        >
          {loading
            ? t("auth.forgot_password.form.sending", "Sending...")
            : t("auth.forgot_password.form.submit", "Send Reset Link")}
        </button>
      </div>
    </form>
  );
}

